/** The Financial Services workspace groups each Magic Insights question in one place. */
export type FsMagicCategory =
  | 'company'
  | 'scale'
  | 'activity'
  | 'fx'
  | 'credit'
  | 'lending'
  | 'working'
  | 'ownership'
  | 'triggers'
  | 'cash'
  | 'green'
  | 'advisers'
  | 'workspace';

export interface FsMagicRelevanceFilter {
  fieldName?: string;
  fsDisplayLabel?: string;
  label?: string;
}

/** The Financial Services module, which also carries the six shared FX questions. */
export const FS_MAGIC_MODULE_NAME = 'Magic Insights - Financial Services';

/**
 * The module's questions have no Data API keys and their IDs differ between environments, so questions are
 * routed by their stable filter field names. Array fields are matched by the array name before "__".
 */
const FS_FIELD_CATEGORIES: ReadonlyArray<[RegExp, FsMagicCategory]> = [
  // Credit appetite: AI credit score (FX 207) and credit stress signals (F11).
  [/^(estimated_credit_worthiness_score|stress_level|stress_signals__)/, 'credit'],
  // FX, trade and payments: FX flows (F12), hedging and providers (F13), trade finance (F6), currency exposure (FX 215)
  // and international operations (FX 222).
  [/^(estimated_annual_fx_volume|currency_flows__|flow_types|natural_hedge|fx_opportunity_score|fx_risk_level|hedging_status|fx_and_payment_providers__|recommended_fx_products|hedging_instruments__)/, 'fx'],
  [/^(trade_finance_likelihood_score|likely_trade_instruments|trade_flows__|influencing_factors)/, 'fx'],
  [/^(currency_volume_distribution__|currency_distribution__|countries_broken_down_by_)/, 'fx'],
  // Lending: debt and facilities (F1) and product needs (F3).
  [/^(facilities__|earliest_maturity_year|total_disclosed_borrowings_gbp|needs__|top_need)/, 'lending'],
  // Customers, working capital and assets: working capital (F4) and assets and capex (F5).
  [/^(b2b_revenue_percentage|customer_types|customer_concentration|typical_customer_payment_terms_days|seasonality_score|peak_trading_months|working_capital_pressures__)/, 'working'],
  [/^(key_assets__|planned_investments__|asset_finance_likelihood_score|capex_next_12_months_)/, 'working'],
  // Ownership: ownership and exit (F7), acquisitions and growth (F8) and group structure (FX 233).
  [/^(ownership_type|sale_likelihood_score|likely_transaction|succession_signals|acquisition_likelihood_score|recent_acquisitions__|growth_plans__)/, 'ownership'],
  [/^(immediate_parent_orgs__|ultimate_beneficial_owners__|summary_of_group_structure)/, 'ownership'],
  // Trigger events (F9).
  [/^events__/, 'triggers'],
  // Cash, treasury and finance team (F10).
  [/^(surplus_cash_assessment|treasury_sophistication|treasury_summary|treasury_needs|finance_decision_makers__|open_finance_roles__)/, 'cash'],
];

export function fsFieldCategory(fieldName: string): FsMagicCategory | null {
  return FS_FIELD_CATEGORIES.find(([pattern]) => pattern.test(fieldName))?.[1] || null;
}

/**
 * Route each Financial Services question once, using its stable field names. Questions from other modules, and new
 * fields that are not mapped yet, stay visible in Your workspace until reviewed.
 */
export function fsMagicCategory(
  moduleName: string,
  _questionId: number,
  filters: ReadonlyArray<FsMagicRelevanceFilter>,
): FsMagicCategory {
  if (moduleName !== FS_MAGIC_MODULE_NAME) return 'workspace';
  const categories = new Set(filters.map(filter => fsFieldCategory(filter.fieldName || ''))
    .filter((category): category is FsMagicCategory => Boolean(category)));
  return categories.size === 1 ? [...categories][0] : 'workspace';
}
